import React, { useState, useEffect, FormEvent } from 'react';
import { apiClient } from '../api/client';
import { usePageAuthorization } from '../hooks/usePageAuthorization';

interface Expenditure {
  id: string;
  description: string;
  amount: number;
  date: string;
  payee: string;
}

interface BudgetItem {
  id: string;
  category: string;
  description: string;
  plannedAmount: number;
  expenditures: Expenditure[];
  actualSpend?: number;
  variance?: number;
}

interface BudgetSummary {
  totalPlanned: number;
  totalActual: number;
  variance: number;
}

const styles: Record<string, React.CSSProperties> = {
  page: { padding: '2rem', maxWidth: '1200px', margin: '0 auto' },
  title: { color: '#1a6b3c', fontSize: '1.75rem', margin: '0 0 1.5rem 0' },
  section: { marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  sectionTitle: { color: '#0f2d1f', fontSize: '1.2rem', marginBottom: '1rem', borderBottom: '2px solid #c9a84c', paddingBottom: '0.25rem' },
  btn: { padding: '0.5rem 1rem', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', marginRight: '0.5rem' },
  btnGold: { padding: '0.5rem 1rem', backgroundColor: '#c9a84c', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' },
  input: { padding: '0.5rem 0.75rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem', boxSizing: 'border-box' as const },
  summaryCards: { display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' as const },
  summaryCard: { flex: '1', minWidth: '180px', padding: '1.25rem', backgroundColor: '#f5f5f5', borderRadius: '8px', textAlign: 'center' as const },
  summaryValue: { fontSize: '1.5rem', fontWeight: 700, color: '#1a6b3c' },
  summaryLabel: { fontSize: '0.8rem', color: '#666', marginTop: '0.25rem' },
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: '0.9rem' },
  th: { textAlign: 'left' as const, padding: '0.75rem', borderBottom: '2px solid #1a6b3c', color: '#0f2d1f', fontWeight: 600 },
  td: { padding: '0.75rem', borderBottom: '1px solid #eee' },
  overBudget: { backgroundColor: '#fdecea' },
  error: { color: '#d32f2f', backgroundColor: '#fdecea', padding: '0.5rem 0.75rem', borderRadius: '4px', marginBottom: '1rem', fontSize: '0.85rem' },
  field: { marginBottom: '1rem' },
  label: { display: 'block', marginBottom: '0.25rem', fontWeight: 600, fontSize: '0.85rem', color: '#333' },
  formRow: { display: 'flex', gap: '0.75rem', flexWrap: 'wrap' as const, alignItems: 'flex-end', marginTop: '1rem' },
};

export function BudgetPage() {
  const { canEdit } = usePageAuthorization('budget');
  const [items, setItems] = useState<BudgetItem[]>([]);
  const [summary, setSummary] = useState<BudgetSummary | null>(null);
  const [error, setError] = useState('');
  const [showAddItem, setShowAddItem] = useState(false);
  const [showAddExpenditure, setShowAddExpenditure] = useState<string | null>(null);

  // Add item form
  const [itemCategory, setItemCategory] = useState('');
  const [itemDesc, setItemDesc] = useState('');
  const [itemPlanned, setItemPlanned] = useState('');

  // Add expenditure form
  const [expDesc, setExpDesc] = useState('');
  const [expAmount, setExpAmount] = useState('');
  const [expDate, setExpDate] = useState('');
  const [expPayee, setExpPayee] = useState('');

  async function fetchData() {
    try {
      const [budgetItems, budgetSummary] = await Promise.all([
        apiClient<BudgetItem[]>('/budget/items'),
        apiClient<BudgetSummary>('/budget/summary').catch(() => null),
      ]);
      setItems(budgetItems);
      setSummary(budgetSummary);
    } catch {
      setError('Failed to load budget data.');
    }
  }

  useEffect(() => { fetchData(); }, []);

  async function handleAddItem(e: FormEvent) {
    e.preventDefault();
    if (!itemCategory || !itemDesc || !itemPlanned) { setError('All fields required.'); return; }
    try {
      await apiClient('/budget/items', { method: 'POST', body: { category: itemCategory, description: itemDesc, plannedAmount: parseFloat(itemPlanned) } });
      setItemCategory(''); setItemDesc(''); setItemPlanned('');
      setShowAddItem(false);
      fetchData();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to add budget item.');
    }
  }

  async function handleAddExpenditure(e: FormEvent, budgetItemId: string) {
    e.preventDefault();
    if (!expDesc || !expAmount || !expDate || !expPayee) { setError('All expenditure fields required.'); return; }
    try {
      await apiClient(`/budget/items/${budgetItemId}/expenditures`, {
        method: 'POST',
        body: { description: expDesc, amount: parseFloat(expAmount), date: expDate, payee: expPayee },
      });
      setExpDesc(''); setExpAmount(''); setExpDate(''); setExpPayee('');
      setShowAddExpenditure(null);
      fetchData();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to add expenditure.');
    }
  }

  async function handleExportCsv() {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/budget/export?format=csv`,
        { headers: { Authorization: `Bearer ${document.cookie}` } }
      );
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'budget-report.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('Failed to export CSV.');
    }
  }

  function getActual(item: BudgetItem): number {
    if (item.actualSpend !== undefined) return item.actualSpend;
    return item.expenditures?.reduce((sum, exp) => sum + Number(exp.amount), 0) ?? 0;
  }

  function getVariance(item: BudgetItem): number {
    if (item.variance !== undefined) return item.variance;
    return Number(item.plannedAmount) - getActual(item);
  }

  return (
    <div style={styles.page}>
      <h1 style={styles.title}>Budget</h1>
      {error && <div style={styles.error}>{error}</div>}

      {/* Summary Cards */}
      <div style={styles.summaryCards}>
        <div style={styles.summaryCard}>
          <div style={styles.summaryValue}>${(summary?.totalPlanned ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
          <div style={styles.summaryLabel}>Total Planned</div>
        </div>
        <div style={styles.summaryCard}>
          <div style={{ ...styles.summaryValue, color: '#0f2d1f' }}>${(summary?.totalActual ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
          <div style={styles.summaryLabel}>Total Actual</div>
        </div>
        <div style={styles.summaryCard}>
          <div style={{ ...styles.summaryValue, color: (summary?.variance ?? 0) < 0 ? '#d32f2f' : '#1a6b3c' }}>
            ${Math.abs(summary?.variance ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
            {(summary?.variance ?? 0) < 0 ? ' over' : ' under'}
          </div>
          <div style={styles.summaryLabel}>Variance</div>
        </div>
      </div>

      {/* Budget Items Table */}
      <div style={styles.section}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 style={{ ...styles.sectionTitle, marginBottom: 0, borderBottom: 'none', paddingBottom: 0 }}>Budget Items</h2>
          <div>
            {canEdit && <button style={styles.btn} onClick={() => setShowAddItem(!showAddItem)}>+ Add Item</button>}
            <button style={styles.btnGold} onClick={handleExportCsv}>Export CSV</button>
          </div>
        </div>

        {showAddItem && (
          <form onSubmit={handleAddItem} style={{ padding: '1rem', backgroundColor: '#f9f9f9', borderRadius: '6px', marginBottom: '1rem' }}>
            <div style={styles.formRow}>
              <div style={styles.field}>
                <label style={styles.label}>Category</label>
                <input style={styles.input} value={itemCategory} onChange={(e) => setItemCategory(e.target.value)} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Description</label>
                <input style={styles.input} value={itemDesc} onChange={(e) => setItemDesc(e.target.value)} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Planned Amount</label>
                <input type="number" step="0.01" min="0.01" style={styles.input} value={itemPlanned} onChange={(e) => setItemPlanned(e.target.value)} />
              </div>
              <button type="submit" style={styles.btn}>Add</button>
            </div>
          </form>
        )}

        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Category</th>
              <th style={styles.th}>Description</th>
              <th style={styles.th}>Planned</th>
              <th style={styles.th}>Actual</th>
              <th style={styles.th}>Variance</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const actual = getActual(item);
              const variance = getVariance(item);
              const isOver = variance < 0;
              return (
                <React.Fragment key={item.id}>
                  <tr style={isOver ? styles.overBudget : {}}>
                    <td style={styles.td}>{item.category}</td>
                    <td style={styles.td}>{item.description}</td>
                    <td style={styles.td}>${Number(item.plannedAmount).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                    <td style={styles.td}>${actual.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                    <td style={{ ...styles.td, color: isOver ? '#d32f2f' : '#1a6b3c', fontWeight: 600 }}>
                      {isOver ? '-' : '+'}${Math.abs(variance).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </td>
                    <td style={styles.td}>
                      {canEdit && (
                      <button
                        style={{ ...styles.btn, fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}
                        onClick={() => setShowAddExpenditure(showAddExpenditure === item.id ? null : item.id)}
                      >
                        + Expenditure
                      </button>
                      )}
                    </td>
                  </tr>
                  {showAddExpenditure === item.id && (
                    <tr>
                      <td colSpan={6} style={{ padding: '1rem', backgroundColor: '#f9f9f9' }}>
                        <form onSubmit={(e) => handleAddExpenditure(e, item.id)} style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                          <div>
                            <label style={styles.label}>Description</label>
                            <input style={styles.input} value={expDesc} onChange={(e) => setExpDesc(e.target.value)} />
                          </div>
                          <div>
                            <label style={styles.label}>Amount</label>
                            <input type="number" step="0.01" min="0.01" style={styles.input} value={expAmount} onChange={(e) => setExpAmount(e.target.value)} />
                          </div>
                          <div>
                            <label style={styles.label}>Date</label>
                            <input type="date" style={styles.input} value={expDate} onChange={(e) => setExpDate(e.target.value)} />
                          </div>
                          <div>
                            <label style={styles.label}>Payee</label>
                            <input style={styles.input} value={expPayee} onChange={(e) => setExpPayee(e.target.value)} />
                          </div>
                          <button type="submit" style={styles.btn}>Add</button>
                        </form>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        {items.length === 0 && <p style={{ color: '#666', marginTop: '1rem' }}>No budget items yet.</p>}
      </div>
    </div>
  );
}
